/**
 * ESDA charts — exploratory spatial data analysis side panels.
 * (Grekousis ch. 2; Mitchell Vol 1; gap analysis G7.)
 *
 * Pure data-prep + inline-SVG builders for the result panel:
 *   - Histogram of a single attribute (links the map's colors to the
 *     distribution — why the class breaks fall where they do).
 *   - Scatter plot for a bivariate map (the picture behind the correlation).
 *   - Statistical outliers (Tukey IQR fences) for narration.
 *
 * No external charting library — SVG is hand-built from numbers and escaped
 * text so it passes DOMPurify with the svg profile. All functions are pure and
 * unit-tested; the SVG builders never interpolate unescaped user text.
 */

/** Escape text for safe inclusion in SVG/HTML. */
function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Collect finite numbers from feature properties. */
export function numericValues(features, attr) {
  const out = []
  for (const f of features || []) {
    const raw = f?.properties?.[attr]
    if (raw == null || raw === '') continue
    const v = Number(raw)
    if (Number.isFinite(v)) out.push(v)
  }
  return out
}

/**
 * Histogram bins via Freedman–Diaconis-ish heuristic, clamped to [5, 20].
 * Returns { bins: [{ x0, x1, count }], maxCount, n } or null. Exported.
 */
export function computeHistogram(values, binCount) {
  const xs = (values || []).filter((v) => Number.isFinite(v))
  const n = xs.length
  if (n < 3) return null
  const min = Math.min(...xs)
  const max = Math.max(...xs)
  if (min === max) return null
  const k = Math.max(5, Math.min(20, Math.round(binCount || Math.sqrt(n))))
  const width = (max - min) / k
  const bins = Array.from({ length: k }, (_, i) => ({
    x0: min + i * width,
    x1: min + (i + 1) * width,
    count: 0,
  }))
  for (const v of xs) {
    let idx = Math.floor((v - min) / width)
    if (idx >= k) idx = k - 1 // max value falls in the last bin
    if (idx < 0) idx = 0
    bins[idx].count++
  }
  const maxCount = bins.reduce((m, b) => Math.max(m, b.count), 0)
  return { bins, maxCount, n, min, max }
}

/** Tukey IQR fences → outliers. Returns { low:[], high:[], lower, upper }. Exported. */
export function detectOutliers(features, attr) {
  const pairs = []
  for (const f of features || []) {
    const raw = f?.properties?.[attr]
    if (raw == null || raw === '') continue
    const v = Number(raw)
    if (Number.isFinite(v))
      pairs.push({
        name: f?.properties?.name || f?.properties?.name_en || f?.properties?.NAME || '',
        v,
      })
  }
  if (pairs.length < 8) return { low: [], high: [], lower: null, upper: null }
  const sorted = pairs.map((p) => p.v).sort((a, b) => a - b)
  const q = (p) => {
    const i = (sorted.length - 1) * p
    const lo = Math.floor(i)
    const hi = Math.ceil(i)
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo)
  }
  const q1 = q(0.25)
  const q3 = q(0.75)
  const iqr = q3 - q1
  const lower = q1 - 1.5 * iqr
  const upper = q3 + 1.5 * iqr
  const low = pairs.filter((p) => p.v < lower).sort((a, b) => a.v - b.v)
  const high = pairs.filter((p) => p.v > upper).sort((a, b) => b.v - a.v)
  return { low, high, lower, upper }
}

/** Narrate outliers in one classroom sentence, or '' if none. Exported. */
export function describeOutliers(outliers, label) {
  if (!outliers) return ''
  const names = (arr) =>
    arr
      .slice(0, 3)
      .map((p) => p.name)
      .filter(Boolean)
  const hi = names(outliers.high)
  const lo = names(outliers.low)
  if (hi.length === 0 && lo.length === 0) return ''
  const parts = []
  if (hi.length) parts.push(`unusually high in ${hi.join(', ')}`)
  if (lo.length) parts.push(`unusually low in ${lo.join(', ')}`)
  return ` ${label} is ${parts.join(', and ')} compared with the rest.`
}

/** Scatter points for a bivariate map. Exported. */
export function computeScatter(features, attrX, attrY) {
  const pts = []
  for (const f of features || []) {
    const rx = f?.properties?.[attrX]
    const ry = f?.properties?.[attrY]
    if (rx == null || ry == null || rx === '' || ry === '') continue
    const x = Number(rx)
    const y = Number(ry)
    if (Number.isFinite(x) && Number.isFinite(y))
      pts.push({ x, y, name: f?.properties?.name || f?.properties?.name_en || '' })
  }
  return pts
}

const W = 260
const H = 150
const PAD = { l: 30, r: 8, t: 8, b: 22 }

/** Inline-SVG histogram card. Exported. */
export function buildHistogramSVG(hist, label) {
  if (!hist) return ''
  const iw = W - PAD.l - PAD.r
  const ih = H - PAD.t - PAD.b
  const bw = iw / hist.bins.length
  let bars = ''
  hist.bins.forEach((b, i) => {
    const h = hist.maxCount > 0 ? (b.count / hist.maxCount) * ih : 0
    const x = PAD.l + i * bw
    const y = PAD.t + (ih - h)
    bars += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${Math.max(0, bw - 1).toFixed(1)}" height="${h.toFixed(1)}" fill="#5B8DEF" opacity="0.85"></rect>`
  })
  const axis = `<line x1="${PAD.l}" y1="${PAD.t + ih}" x2="${W - PAD.r}" y2="${PAD.t + ih}" stroke="#888" stroke-width="1"></line>`
  return (
    `<div class="esda-chart"><div class="esda-chart-title">Distribution of ${esc(label)}</div>` +
    `<svg viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Histogram of ${esc(label)}">` +
    `${bars}${axis}` +
    `<text x="${PAD.l}" y="${H - 6}" font-size="9" fill="#aaa">${esc(fmt(hist.min))}</text>` +
    `<text x="${W - PAD.r}" y="${H - 6}" font-size="9" fill="#aaa" text-anchor="end">${esc(fmt(hist.max))}</text>` +
    `</svg></div>`
  )
}

/** Inline-SVG scatter card with an OLS trend line. Exported. */
export function buildScatterSVG(points, labelX, labelY, corr) {
  if (!points || points.length < 3) return ''
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  const xMin = Math.min(...xs)
  const xMax = Math.max(...xs)
  const yMin = Math.min(...ys)
  const yMax = Math.max(...ys)
  if (xMin === xMax || yMin === yMax) return ''
  const iw = W - PAD.l - PAD.r
  const ih = H - PAD.t - PAD.b
  const sx = (x) => PAD.l + ((x - xMin) / (xMax - xMin)) * iw
  const sy = (y) => PAD.t + ih - ((y - yMin) / (yMax - yMin)) * ih
  let dots = ''
  for (const p of points) {
    dots += `<circle cx="${sx(p.x).toFixed(1)}" cy="${sy(p.y).toFixed(1)}" r="2.2" fill="#5B8DEF" opacity="0.7"></circle>`
  }
  // OLS trend line
  let trend = ''
  const n = points.length
  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = ys.reduce((a, b) => a + b, 0) / n
  let sxy = 0
  let sxx = 0
  for (let i = 0; i < n; i++) {
    sxy += (xs[i] - mx) * (ys[i] - my)
    sxx += (xs[i] - mx) ** 2
  }
  if (sxx > 0) {
    const slope = sxy / sxx
    const b0 = my - slope * mx
    const y1 = slope * xMin + b0
    const y2 = slope * xMax + b0
    trend = `<line x1="${sx(xMin).toFixed(1)}" y1="${sy(y1).toFixed(1)}" x2="${sx(xMax).toFixed(1)}" y2="${sy(y2).toFixed(1)}" stroke="#F5A623" stroke-width="1.5" opacity="0.9"></line>`
  }
  const rLabel = corr && Number.isFinite(corr.r) ? `r = ${corr.r}` : ''
  return (
    `<div class="esda-chart"><div class="esda-chart-title">${esc(labelX)} vs ${esc(labelY)}${rLabel ? ` &middot; ${esc(rLabel)}` : ''}</div>` +
    `<svg viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Scatter plot of ${esc(labelX)} versus ${esc(labelY)}">` +
    `<line x1="${PAD.l}" y1="${PAD.t + ih}" x2="${W - PAD.r}" y2="${PAD.t + ih}" stroke="#888" stroke-width="1"></line>` +
    `<line x1="${PAD.l}" y1="${PAD.t}" x2="${PAD.l}" y2="${PAD.t + ih}" stroke="#888" stroke-width="1"></line>` +
    `${dots}${trend}</svg></div>`
  )
}

function fmt(v) {
  const a = Math.abs(v)
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`
  if (a >= 1) return String(Math.round(v))
  return String(Number(v.toPrecision(2)))
}
